<template>
    <div class="calendar-filters">
        <input
            :value="value"
            class="calendar-filters__input edit-box--input input"
            name="calendar-filter"
            placeholder="Filter"
            @input="$emit('input', $event.target.value)"
        >

        <div class="calendar-filters__spacer"/>

        <button
            :disabled="disableButtons"
            class="button"
            :class="{ 'button-green': filter_only_active }"
            v-if="hasInActiveAccommodations()"
           @click="switch_filter_only"
        >
            {{ $t("generic.only_active") }}
        </button>

        <button
            :disabled="disableButtons || fetchingPmsPrices || fetchingPmsAvailabilities"
            class="button fetch-button"
            v-if="canLoadPmsPrices"
           @click="load_pms_prices"
        >
            {{ !fetchingPmsPrices ? $t("generic.loadPmsPrices") : 'Loading ' }}
            <span v-if="fetchingPmsPrices" class="fetch-spinner">
                <i class="fa fa-spinner fa-spin"></i>
            </span>
        </button>

        <button
            :disabled="disableButtons || fetchingPmsAvailabilities || fetchingPmsPrices"
            class="button fetch-button"
            v-if="canLoadPmsAvailabilities"
           @click="load_pms_availabilities"
        >
            {{ !fetchingPmsAvailabilities ? $t("generic.loadPmsAvailabilities") : 'Loading ' }}
            <span v-if="fetchingPmsAvailabilities" class="fetch-spinner">
                <i class="fa fa-spinner fa-spin"></i>
            </span>
        </button>


        <button
            :disabled="disableButtons"
            class="button tooltip-activator"
            @click="bulkEdit"
        >
            Bulk
        </button>

        <label
            :disabled="disableButtons"
            for="upload-file"
            class="button tooltip-activator"
        >
            <span class="tooltip tooltip--is-bottom-left">{{ $t('form.uploadExcel') }}</span>
            <i class="fa fa-cloud-upload" aria-hidden="true"/>

            <input
                id="upload-file"
                class="d-none"
                type="file"
                @input="uploadExcel($event)"
            />
        </label>

        <button
            :disabled="disableButtons"
            class="button tooltip-activator"
            @click="downloadExcel"
        >
            <span class="tooltip tooltip--is-bottom-left">{{ $t('form.downloadExcel') }}</span>

            <i class="fa fa-cloud-download" aria-hidden="true"/>
        </button>

        <button
            :disabled="disableButtons"
            class="button"
            @click="$emit('collapse-all')"
        >
            {{ $t("generic.open_all_close_all") }}
        </button>

        <Modal
            ref="modal"
            title="Mehrfachauswahl"
            class="calendar-filters__modal"
            :submit-button="$t('generic.save')"
            :on-submit="saveDayPrices"
            :is-button-disabled="isSaveButtonDisabled"
            @closed="resetSelectedCampsite"
        >
            <div class="calendar-filters__multiselect">
                <multiselect
                    v-model="selectedCampsites"
                    :options="filteredResources"
                    group-values="items"
                    group-label="group"
                    :group-select="true"
                    :multiple="true"
                    :searchable="true"
                    :close-on-select="false"
                    :hide-selected="false"
                    selectLabel=""
                    label="name"
                    track-by="name"
                />
            </div>

            <div class="campsite-calendar__date-picker">
                <v-date-picker
                    ref="startDate"
                    v-model="selectedDate.start"
                    v-bind="datePickerOptions"
                    is-required
                    @input="setEndDate(selectedDate.start)"
                >
                    <template #header-title="{ monthLabel, yearLabel }">
                        <a
                            class="campsite-calendar__today-button"
                            @click.stop="setTodayDate('start')"
                        >
                            {{ $t('generic.today') }}
                        </a>
                        <p>{{ `${monthLabel} ${yearLabel}` }}</p>
                    </template>
                    <template v-slot="{ inputValue, showPopover, hidePopover}">
                        <input
                            :value="inputValue"
                            @blur="setEndDate($event, 'start')"
                            @keydown="hideCalendarOnTab($event, hidePopover)"
                            @click="showPopover"
                        />
                    </template>
                </v-date-picker>

                <svg
                    class="campsite-calendar__date-picker-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                </svg>
                <v-date-picker
                    ref="endDate"
                    v-model="selectedDate.end"
                    v-bind="datePickerOptions"
                    is-required
                    @input="setEndDate(selectedDate.end)"
                >
                    <template #header-title="{ monthLabel, yearLabel }">
                        <span>
                        <a
                            class="campsite-calendar__today-button"
                            @click.stop="setTodayDate('end')"
                        >
                            {{ $t('generic.today') }}
                        </a>
                        <a
                            class="campsite-calendar__today-button"
                            style="margin-left: 10px"
                            @click.stop="setMaxDate('end')"
                        >
                            {{ $t('generic.maxDate') }}
                        </a>
                        </span>
                        <p>{{ `${monthLabel} ${yearLabel}` }}</p>
                    </template>
                    <template v-slot="{ inputValue, showPopover, hidePopover }">
                        filter
                        <input
                            :value="inputValue"
                            @blur="setEndDate($event, 'end')"
                            @keydown="hideCalendarOnTab($event, hidePopover)"
                            @click="showPopover"
                        />
                    </template>
                </v-date-picker>
            </div>
            <div>
                <ul>
                    <template v-for="(resource, i) in filteredExtras">
                        <li
                            v-if="!resource.readonly"
                            :key="resource.name"
                            :id="`resource-${resource.id}`"
                            class="campsite-calendar__form-group"
                            :class="{ 'campsite-calendar__form-group--is-disabled': isInputDisabled(resource) }"
                        >
                            <label
                                class="campsite-calendar__form-label"
                                :for="resource.id"
                            >
                                {{ i === 0 ? 'Preis pro Nacht' : resource.name }}
                            </label>
                            <span class="campsite-calendar__form-input-wrapper">
                                    <multiselect
                                        v-if="resource.input_type === 'bool' && isMultiSelect(resource)"
                                        v-model="formData[resource.id]"
                                        :name="resource.id"
                                        class="campsite-calendar__form-input-field"
                                        :options="multiSelectOptions"
                                        track-by="code"
                                        label="label"
                                        :close-on-select="false"
                                        :multiple="true"
                                        :clear-on-select="false"
                                        :show-labels="false"
                                        @open="valueChanged(resource)"

                                    ></multiselect>
                                    <select
                                        v-else-if="resource.input_type === 'bool' && ! isMultiSelect(resource)"
                                        v-model="formData[resource.id]"
                                        :name="resource.id"
                                        class="campsite-calendar__form-input-field"
                                        @change="valueChanged(resource)"
                                    >
                                        <option/>
                                        <option value="true">{{ $t('generic.yes') }}</option>
                                        <option value="false">{{ $t('generic.no') }}</option>
                                    </select>
                                    <input
                                        v-else
                                        v-model="formData[resource.id]"
                                        class="campsite-calendar__form-input-field"
                                        :id="resource.id"
                                        :type="getInputType(resource)"
                                        @input="valueChanged(resource)"
                                        @keypress="inputBinding($event, resource)"
                                    >
                                </span>
                            <label
                                class="switch is-success"
                                :class="{'checked': !isInputDisabled(resource)}"
                                @click="toggleInputState(resource)"
                            >
                                <input
                                    type="checkbox"
                                    :name="'btn_' + resource.id"
                                    tabindex="-1"
                                    :checked="!isInputDisabled(resource)"
                                >
                            </label>
                        </li>
                    </template>
                </ul>
            </div>
        </Modal>
    </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import {Api} from '../../../api';
import {EventBus} from '../../../utils';
import {Modal} from '../../components';
import moment from 'moment/moment';
import {AppModel} from '../../../models';
import { cloneDeep } from 'lodash';

export default {
    name: 'calendar-filters',
    props: ['value', 'resources', 'extras'],
    components: {
        Multiselect,
        Modal,
    },
    data() {
        return {
            replaceKey: 'TO_BE_REPLACED',
            filter_only_active: false,
            file: null,
            formData: {},
            selectedCampsites: [],
            disableButtons: false,
            fetchingPmsPrices: false,
            fetchingPmsAvailabilities: false,
            selectedDate: {
                end: new Date(),
                start: new Date(),
            },
            datePickerOptions: {
                minDate: new Date(),
                maxDate: new Date(AppModel.activeCampground.pricelist_ranges.max),
            },
            enabledInputFieldIds: [],
            multiSelectOptions: [
                {'code': 255, 'label': 'Alle'},
                {'code': -255, 'label': 'Keine'},
                {'code': 1, 'label': 'Mo'},
                {'code': 2, 'label': 'Di'},
                {'code': 3, 'label': 'Mi'},
                {'code': 4, 'label': 'Do'},
                {'code': 5, 'label': 'Fr'},
                {'code': 6, 'label': 'Sa'},
                {'code': 7, 'label': 'So'}],
        };
    },
    computed: {
        filteredResources() {
            const resources = {
                group: 'All',
                items: [],
            };

            this.resources.forEach((resource) => {
                resources.items.push(resource[0]);
            });

            return [resources];
        },
        filteredExtras() {
            const allExtras = [];
            const occurrencesOfExtras = {};
            const extras = [];
            const selectedCampsitesAmount = this.selectedCampsites.length;

            // Add price per night
            if(selectedCampsitesAmount) {
                const extra = {...this.selectedCampsites[0]};
                const id = extra.id.split(':').pop();

                extra.id = extra.id.replace(id, this.replaceKey);
                extras.push(extra);
            }

            // Add all available extras
            this.selectedCampsites.forEach((campsite) => {
                this.extras[campsite.id].forEach((extra) => {
                    const formattedExtra = cloneDeep(extra);
                    const id = campsite.id.split(':').pop();

                    formattedExtra.id = formattedExtra.id.replace(id, this.replaceKey);
                    allExtras.push(formattedExtra);
                });

                // allExtras.push(...this.extras[campsite.id]);
            });

            // Count amount of extras that all campsites have in common
            allExtras.forEach((extra) => {
                if(occurrencesOfExtras[extra.name]) {
                    occurrencesOfExtras[extra.name]++;
                } else {
                    occurrencesOfExtras[extra.name] = 1;
                }
            });

            // Filter for all common extras
            for(const [key, value] of Object.entries(occurrencesOfExtras)) {
                if(value === selectedCampsitesAmount) {
                    extras.push(allExtras.find((extra) => extra.name === key));
                }
            }

            return extras;
        },
        isSaveButtonDisabled() {
            return Object.keys(this.formData).length === 0;
        },
        canLoadPmsPrices() {
            return AppModel.activeCampground.can_fetch_prices_from_pms;
        },
        canLoadPmsAvailabilities() {
            return AppModel.activeCampground.can_fetch_availabilities_from_pms;
        }
    },
    mounted() {
        if(this.hasInActiveAccommodations()) {
            this.switch_filter_only();
        }
    },
    methods: {
        hideCalendarOnTab(event, hideFn) {
            if(event.code.toLowerCase() === 'tab') {
                setTimeout(() => {
                    hideFn();
                }, 150);
            }
        },
        setEndDate(date, phase) {
            if(phase) {
                date = date.target.value;
                date = moment(date, 'DD.MM.YYYY');
                date = new Date(date.format());

                if(date === 'Invalid Date') {
                    return;
                }

                this.selectedDate[phase] = date;
            }

            if(date.getTime() > this.selectedDate.end.getTime()) {
                this.selectedDate.end = date;
            }
        },
        async downloadExcel() {
            this.$emit('excel-downloading');
            this.disableButtons = true;
            const {data} = (await Api.dayPrices.downloadExcel());
            const href = URL.createObjectURL(data);
            const link = document.createElement('a');

            link.href = href;
            link.setAttribute('download', 'pricelist-' + AppModel.activeCampground.slug + '.xlsx');
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(href);
            this.$emit('excel-downloaded');
            this.disableButtons = false;
        },
        async uploadExcel(event) {
            const formData = new FormData();
            const file = event.target.files[0];

            formData.append('file', file);

            this.$emit('file-uploading');

            try {
                const response = await Api.dayPrices.uploadExcel(formData);
                if(response.status >= 400) {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: this.$t('generic.calendar.notifications.failed.title'),
                        body: response.body.errors,
                        type: this.$t('generic.calendar.notifications.failed.type'),
                        direction: 'Up',
                        duration: 30000,
                    });
                } else {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: this.$t('generic.calendar.notifications.updated.title'),
                        body: this.$t('generic.calendar.notifications.updated.msg'),
                        type: this.$t('generic.calendar.notifications.updated.type'),
                        direction: 'Up',
                        duration: 3000,
                    });
                }
            } catch (e) {
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                    title: this.$t('generic.calendar.notifications.failed.title'),
                    body: this.$t('generic.calendar.notifications.failed.msg'),
                    type: this.$t('generic.calendar.notifications.failed.type'),
                    direction: 'Up',
                    duration: 3000,
                });
            } finally {
                this.$emit('file-uploaded');
                event.target.value = null;
            }
        },
        setTodayDate(selectedDateKey) {
            const date = new Date();
            this.selectedDate[selectedDateKey] = date;
            this.$refs[`${selectedDateKey}Date`].move(date);
        },
        setMaxDate(selectedDateKey) {
            const date = new Date(AppModel.activeCampground.pricelist_ranges.max);
            this.selectedDate[selectedDateKey] = date;
            this.$refs[`${selectedDateKey}Date`].move(date);
        },
        valueChanged(resource) {
            if(this.enabledInputFieldIds.indexOf(resource.id) === -1) {
                this.toggleInputState(resource);
            }
        },
        isMultiSelect(resource) {
            return (resource.id.indexOf('_can_arrive') > 0 || resource.id.indexOf('_can_departure') > 0);
        },
        getInputType(resource) {
            switch (resource.input_type) {
            case 'bool':
                return 'checkbox';
            case 'int':
                return 'number';
            case 'price':
                return 'number';
            default:
                return 'text';
            }
        },
        inputBinding(event, resource) {
            const char = event.key;
            const regex = /[.,-]/g;

            if(resource.input_type === 'int') {
                if(regex.test(char)) {
                    event.preventDefault();
                }
            }
        },
        isInputDisabled(resource) {
            const index = this.enabledInputFieldIds.indexOf(resource.id);

            return index === -1;
        },
        toggleInputState(resource) {
            const index = this.enabledInputFieldIds.indexOf(resource.id);

            if(index === -1) {
                this.enabledInputFieldIds.push(resource.id);
            } else {
                this.enabledInputFieldIds.splice(index, 1);
                this.$delete(this.formData, resource.id);
            }
        },
        bulkEdit() {
            this.$refs.modal.show = true;
        },
        resetSelectedCampsite() {
            this.selectedCampsites = [];
            this.formData = {};
            this.selectedDate = {
                end: new Date(),
                start: new Date(),
            };
            this.enabledInputFieldIds = [];
        },
        hasInActiveAccommodations() {
            const isInactiveResource = (resource) => !resource[0].active;
            return this.resources.some(isInactiveResource);
        },
        async saveDayPrices() {
            for(let i = 0; i < this.selectedCampsites.length; i++) {
                const campsite = this.selectedCampsites[i].id.split(':').pop();
                const start = moment(this.selectedDate.start).format('YYYY-MM-DD');
                const end = moment(this.selectedDate.end).format('YYYY-MM-DD');
                const data = [];

                for(const key in this.formData) {
                    data.push({
                        resource: key.replace(this.replaceKey, campsite),
                        value: this.formData[key]
                    });
                }

                await Api.dayPrices.setDayPrices({
                    campsite,
                    start,
                    end,
                    data,
                });
            }

            this.$emit('bulk-complete');
        },
        switch_filter_only() {
            this.filter_only_active = !this.filter_only_active;
            this.$emit('filter-only-active', this.filter_only_active);
        },
        async load_pms_prices() {
            this.fetchingPmsPrices = true;
            const result = await Api.resources.fetchPmsPrices();
            if(result && result.status === 200) {
                this.$emit('bulk-complete');
                if(result.data.status === 'ERROR') {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`,
                        direction: 'Up',
                        type: 'danger',
                        body: this.$t('generic.pmsPriceLoadedError', { msg: result.data.msg })
                    });
                } else {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`,
                        direction: 'Up',
                        body: this.$t('generic.pmsPriceLoadedOkay', { count: result.data.updated })
                    });
                }
            }
            this.fetchingPmsPrices = false;
        },
        async load_pms_availabilities() {
            this.fetchingPmsAvailabilities = true;
            const result = await Api.resources.fetchPmsAvailabilities();
            if(result && result.status === 200) {
                this.$emit('bulk-complete');
                if(result.data.status === 'ERROR') {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`,
                        direction: 'Up',
                        type: 'danger',
                        body: this.$t('generic.pmsPriceLoadedError', { msg: result.data.msg })
                    });
                } else {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`,
                        direction: 'Up',
                        body: this.$t('generic.pmsPriceLoadedOkay', { count: result.data.updated })
                    });
                }
            }
            this.fetchingPmsAvailabilities = false;
        }

    }
};
</script>

<style lang="scss" scoped src="./calendar-filters.scss"/>
<style scoped>
.button-green {
    background-color: #4CAF50;
    color: #ffffff;
}
.button-green:hover {
    background-color: #45a049; /* Wir ändern die Farbe beim Darüberfahren ein wenig, um einen Hover-Effekt zu erzeugen */
}
.fetch-spinner {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    margin-left: 10px;
}

.fetch-button {
    display: inline-flex;
    align-items: center;
    min-width: 150px;
}

</style>



// WEBPACK FOOTER //
// src/views/components/calendar-filters/calendar-filters.vue